import React, { useCallback, useEffect } from "react";
import { useAppContext } from "../../../contexts/appContext";
import { useDataEvents } from "../../../providers/dataEvents";
import { EventTypes } from "../../../providers/types";
import { IconButton } from "../../buttons/iconButton";
import { StorageKeys, useLocalStorage } from "../../../hooks/useLocalStorage";
import { Attendee } from "../../../types";
import { ActionType } from "../../../contexts/types";

type LaserButtonProps = {
  toolTip: "top" | "bottom" | "right" | "left" | "none";
  emitClick?: (e) => void;
};

export const LaserPointerButton: React.FC<LaserButtonProps> = ({
  toolTip,
  emitClick,
}) => {
  const { sendEvent } = useDataEvents();
  const { isInstructorOrModerator, state, dispatch } = useAppContext();
  const { getLocalStorage } = useLocalStorage();
  const locAttendee: Attendee = getLocalStorage(StorageKeys.attendee);

  const laserAction = useCallback(
    (e) => {
      // Set default payload for dispatch and sending event
      const eventPayload = {
        laserInstructor: locAttendee?.AttendeeId,
        showLaser: state.showLaser,
      };
      let dispatchAction = ActionType.EVENT_TOGGLE_LASER_POINTER;

      // If laser is off, then turn it on and assign user as the laser instructor
      if (
        !state.showLaser &&
        state.laserInstructor !== locAttendee?.AttendeeId
      ) {
        eventPayload.showLaser = true;
      }
      // if the laser is on...
      else if (state.showLaser) {
        // ...and the user is not the laser instructor, then update laser instructor to user
        if (state.laserInstructor !== locAttendee?.AttendeeId) {
          dispatchAction = ActionType.UPDATE_LASER_INSTRUCTOR;
        }
        // ...and the user is the laser instructor, then turn off the laser
        else {
          eventPayload.showLaser = false;
          eventPayload.laserInstructor = undefined;
        }
      }

      // Update state and send event
      dispatch({ type: dispatchAction, payload: eventPayload });
      sendEvent(
        {
          type: EventTypes.TOGGLE_LASER_POINTER,
          showLaser: eventPayload.showLaser,
          leader: isInstructorOrModerator,
          exploreMode: eventPayload.showLaser ? false : state.exploreMode,
          laserView: {
            activeInstructor: eventPayload.laserInstructor,
            x: undefined,
            y: undefined,
            instHeight: undefined,
            instWidth: undefined,
          },
        },
        state
      );

      emitClick && emitClick(e);
    },
    [
      dispatch,
      emitClick,
      isInstructorOrModerator,
      locAttendee?.AttendeeId,
      sendEvent,
      state,
    ]
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only toggle the laser pointer if a input or text area aren't active
      const activeTagName = document?.activeElement?.tagName;
      const isFormChild = document?.activeElement?.closest("form");

      if (
        activeTagName !== "INPUT" &&
        activeTagName !== "TEXTAREA" &&
        !isFormChild
      ) {
        // Your code for n
        switch (event.key.toLowerCase()) {
          case "l":
          case "L":
            laserAction(event);
            break;

          default:
            return;
        }
        // Prevent default browser behavior
        event.preventDefault();
      }
    };

    // Add event listener to document or specific element
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [state, laserAction]);

  return (
    <IconButton
      onClick={laserAction}
      iconName="laser"
      desc={
        state.showLaser && locAttendee?.AttendeeId !== state.laserInstructor
          ? "Laser pointer is active by another instructor, click to take control"
          : state.showLaser
          ? "Laser pointer"
          : "Laser pointer"
      }
      toolTip={toolTip}
      size="28px"
      selected={state.showLaser}
    />
  );
};
